// loadRevisionHistory.js — shared helper used by PrintReport and ViewTest
// to fetch all revisions for a test and resolve editor display names.
//
// Returns:
//   {
//     revisions: [...],          // array of revision docs sorted asc by revisionNumber
//     editorNames: { uid: name } // map for displaying "Editor" column in audit table
//   }
//
// Editor name lookups are deduplicated — each unique UID across all revisions
// triggers exactly one getUserProfile call, run in parallel.
//
// The audit table is only meaningful for tests with 2+ revisions; callers
// should check `revisions.length > 1` before rendering it.

import { getAllRevisions, getUserProfile } from "../firestoreService";

export async function loadRevisionHistory(testId) {
  if (!testId) return { revisions: [], editorNames: {} };

  const revisions = await getAllRevisions(testId);
  if (!revisions || revisions.length === 0) {
    return { revisions: [], editorNames: {} };
  }

  // Collect unique editor UIDs across all revisions
  const uniqueUids = [
    ...new Set(
      revisions
        .map((r) => r.createdByUid)
        .filter((uid) => uid && typeof uid === "string"),
    ),
  ];

  // Parallel lookup, dedupe via Promise.all
  const profiles = await Promise.all(
    uniqueUids.map(async (uid) => {
      try {
        const profile = await getUserProfile(uid);
        return [uid, profile?.displayName || "Unknown user"];
      } catch (e) {
        console.warn(`Failed to look up editor ${uid}:`, e);
        return [uid, "Unknown user"];
      }
    }),
  );

  const editorNames = Object.fromEntries(profiles);
  return { revisions, editorNames };
}
